import { useMutation } from "@tanstack/react-query";
import {
  ColumnFiltersState,
  createColumnHelper,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { makeServiceTierColumn } from "../../components/Table/serviceTier";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { HouseholdWithMetrics } from "../../../../api/src/households/households.service";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import type { Model } from "../../../../api/src/models/models.service";
import type { ModelAssignmentStrategy } from "../../../../api/src/firms/firms.service";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import Loading from "../../Loading";
import { StrategyBadge } from "../../components/ModelSourceBadge";
import ActionButton from "../../components/ActionButton";
import { useErrorMessage } from "../../components/FormError";
import IndeterminateCheckbox from "../../components/IndeterminateCheckbox";
import ModelAssignDialog from "../../components/ModelAssignDialog";
import SettlementWarning from "../../components/SettlementWarning";
import TabContainerWithTabs from "../../components/TabContainer";
import { Table, useTable } from "../../components/Table/Table";
import { useCustomFieldsFilter } from "../../components/Table/CustomFieldsFilter";
import {
  type CustomFieldFilter,
  type CustomFieldRow,
  hiddenRowIds,
  matchesCustomFieldFilter,
  usableDefinitions,
} from "../../components/Table/customFieldFilter";
import { useQueryHouseholdCustomFields } from "../../components/Table/useQueryHouseholdCustomFields";
import {
  AssignedToFilter,
  multiSelectIncludes,
  multiSelectIncludesAll,
  StatusFilter,
  TagFilter,
} from "../../components/Table/filters";
import {
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,
  type SetTableSettings,
  type TableSettings,
  useTableSettings,
} from "../../components/Table/tableSettings";
import {
  useInsightHouseholdIds,
  type InsightKey,
} from "../../insights/insightRegistry";
import { useQueryCrmUsers } from "../../firm/lib";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import { formatCurrency } from "../../lib/numbers";
import { useQueryModels } from "../../models/lib";
import ManageNav from "../ManageNav";
import {
  displayHouseholdStatus,
  useQueryExportHouseholds,
  useQueryHouseholds,
} from "./lib";

type HouseholdRow = Pick<
  HouseholdWithMetrics,
  | "id"
  | "name"
  | "householdBalance"
  | "cashBalance"
  | "isActive"
  | "latestRebalanceId"
  | "latestRebalanceDate"
  | "tags"
  | "serviceTier"
> & {
  model?: Model;
  modelAssignmentStrategy: ModelAssignmentStrategy | null;
  assignedUser?: { id: number | string; name: string };
};

const columnHelper = createColumnHelper<HouseholdRow>();

// ── Service Tier (Slant) — column, sorting, two-level filter (R5/R6/R7) ────────
// s-slant-client-tier-as-019ec6dd. The implementation is shared with the four
// rebalance tables (J6) in `components/Table/serviceTier`. This module binds the
// generic column factory to `HouseholdRow` and re-exports the helpers so the J4
// regression tests keep importing them from here.
export {
  hasSlantConnection,
  serviceTierSortingFn,
  deriveServiceTierBooks,
  deriveTierOptions,
  serviceTierFilterFn,
  ServiceTierFilter,
  type ServiceTierFilterValue,
} from "../../components/Table/serviceTier";

// `serviceTierColumn` is the HouseholdRow-bound column built from the shared
// factory; the J4 spec imports this name from here.
export const serviceTierColumn = makeServiceTierColumn(columnHelper);

const columns = [
  columnHelper.accessor((row) => row.name, {
    id: "name",
    cell: (info) => (
      <Link to={`/clients/households/${info.row.original.id}`}>
        {info.getValue()}
      </Link>
    ),
    header: () => "Name",
    minSize: 225,
  }),
  columnHelper.accessor((row) => row.householdBalance ?? 0, {
    id: "householdBalance",
    cell: (info) => formatCurrency(info.getValue()),
    header: () => "AUM",
    enableColumnFilter: false,
    meta: {
      className: "text-end",
      headerClassName: "text-end",
    },
  }),
  columnHelper.accessor((row) => row.cashBalance ?? 0, {
    id: "cashBalance",
    cell: (info) => formatCurrency(info.getValue()),
    header: () => "Cash*",
    enableColumnFilter: false,
    meta: {
      className: "text-end",
      headerClassName: "text-end",
    },
  }),
  columnHelper.accessor((row) => row.model?.name ?? "", {
    id: "model",
    cell: (info) =>
      info.getValue() === "" ? (
        ""
      ) : (
        <>
          <Link to={`/models/models/${info.row.original.model?.id}`}>
            {info.getValue()}
          </Link>
          {info.row.original.modelAssignmentStrategy &&
            info.row.original.modelAssignmentStrategy !== "fallback" && (
              <span className="ms-2">
                <StrategyBadge
                  strategy={info.row.original.modelAssignmentStrategy}
                />
              </span>
            )}
        </>
      ),
    header: () => "Model",
    minSize: 225,
  }),
  columnHelper.accessor((row) => row.assignedUser?.id.toString() ?? "", {
    id: "assignedUserId",
    cell: (info) => info.row.original.assignedUser?.name ?? "",
    header: () => "Assigned To",
    minSize: 100,
    filterFn: multiSelectIncludes,
    meta: {
      filterComponent: AssignedToFilter,
    },
  }),
  columnHelper.accessor(
    (row) =>
      row.latestRebalanceDate
        ? new Date(row.latestRebalanceDate).toLocaleDateString()
        : "",
    {
      id: "rebalance",
      cell: (info) =>
        info.getValue() === "" ? (
          ""
        ) : (
          <Link to={`/rebalances/${info.row.original.latestRebalanceId}`}>
            {info.getValue()}
          </Link>
        ),
      header: () => "Pending Trade Proposal",
      enableColumnFilter: false,
    },
  ),
  columnHelper.accessor("isActive", {
    cell: (info) => displayHouseholdStatus(info.row.original),
    header: () => "Status",
    meta: {
      filterComponent: StatusFilter,
    },
  }),
  columnHelper.accessor((row) => (row.tags ?? []).map((tag) => tag.name), {
    id: "tags",
    cell: (info) =>
      (info.row.original.tags ?? []).map((tag) => (
        <Badge key={tag.name} bg="secondary" pill className="me-1">
          {tag.name}
        </Badge>
      )),
    header: () => "Tag",
    minSize: 100,
    filterFn: multiSelectIncludesAll,
    meta: {
      filterComponent: TagFilter,
    },
  }),
];

// Resolves the household id filter from the URL: a short `?insight=<key>` token
// re-derives its (large) id list, while explicit `?id=` lists stay supported.
// `isResolving` lets callers hold off rendering until the ids resolve so the
// unfiltered list never flashes (APP-1277).
export const useHouseholdInsightFilter = () => {
  const [params] = useSearchParams();
  const insight = params.get("insight") as InsightKey | null;
  const explicitIds = params.getAll("id").map((id) => parseInt(id));
  const { ids: insightIds, isPending } = useInsightHouseholdIds(insight);
  return {
    filteredIds: insight ? insightIds : explicitIds,
    isResolving: insight !== null && isPending,
  };
};

/**
 * Whether the user is connected to Slant (`connections.slant` on GET
 * /profile), which decides whether the Service Tier column shows. The rows
 * cannot say: `Household` always carries `serviceTier`, `null` when there is
 * no tier (APP-1648).
 */
const useSlantConnected = () => {
  const { data: profileBody } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  return profileBody?.data.connections.slant === true;
};

const householdRowId = (row: { id: number }) => row.id.toString();

/**
 * The custom field filter of Manage › Households and Dashboard › Client
 * Summary (spec `doc-custom-field-filtering-01a11cc6e668eb59`, R14, R30, R32
 * to R34, R37, section 5.5). Both tables save their settings under the key
 * `households`, so they share the filter.
 *
 * The filter is the `customFieldFilter` entry of the table's
 * `useTableSettings` state, and "Apply (n)" saves it only through that hook's
 * callback, in its updater form: a separate write is lost on the next column
 * filter, sort or page size change, and the mobile "Clear" clears the column
 * filters and this filter in one handler, so each write must start from the
 * latest settings. "Apply (n)" also moves the table to its first page, because
 * `autoResetAll: false` keeps the page index, and clears the hidden rows from
 * the selection (R37). The column filters do not change the selection (NG11).
 *
 * `rows` are the rows before the custom field filter; the hook returns the
 * rows that match it.
 */
function useHouseholdsCustomFieldsFilter<
  TRow extends CustomFieldRow & { id: number },
>({
  rows,
  tableSettings,
  setTableSettings,
  setPagination,
  setRowSelection,
}: {
  rows: TRow[];
  tableSettings: TableSettings;
  setTableSettings: SetTableSettings;
  setPagination: (updater: (prev: PaginationState) => PaginationState) => void;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}) {
  const definitionsQuery = useQueryHouseholdCustomFields();

  const onApply = useCallback(
    (next: CustomFieldFilter) => {
      setTableSettings((prev) => ({ ...prev, customFieldFilter: next }));
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      if (setRowSelection) {
        const hidden = hiddenRowIds(
          rows,
          next,
          usableDefinitions(definitionsQuery.data ?? []),
          householdRowId,
        );
        if (hidden.length > 0) {
          setRowSelection((prev) => {
            const kept = { ...prev };
            for (const id of hidden) {
              delete kept[id];
            }
            return kept;
          });
        }
      }
    },
    [
      setTableSettings,
      setPagination,
      setRowSelection,
      rows,
      definitionsQuery.data,
    ],
  );

  const { filterBarItems, filter, definitions } = useCustomFieldsFilter({
    definitionsQuery,
    savedFilter: tableSettings.customFieldFilter,
    onApply,
  });

  const shownRows = useMemo(
    () =>
      rows.filter((row) => matchesCustomFieldFilter(row, filter, definitions)),
    [rows, filter, definitions],
  );

  return { filterBarItems, rows: shownRows };
}

/** The rows that the insight id filter of the URL keeps. */
const filterByInsightIds = <TRow extends { id: number }>(
  rows: TRow[],
  filteredIds: number[] | null | undefined,
) =>
  rows.filter(
    (household) =>
      filteredIds == null ||
      filteredIds.length <= 0 ||
      filteredIds.includes(household.id),
  );

// R17 of spec doc-home-households-tab-01a188596d04b371 (APP-2158): a failed
// model or assignee read keeps the household rows and shows these texts above
// the table. Same texts as go/src/households/HouseholdsPage.tsx:109-111.
const MODEL_NAMES_FAILED_TEXT = "The model names did not load";
const ASSIGNEE_NAMES_FAILED_TEXT = "The assignee names did not load";

const SupportingReadAlerts = ({
  isErrorModels,
  isErrorAssignees,
}: {
  isErrorModels: boolean;
  isErrorAssignees: boolean;
}) => (
  <>
    {isErrorModels && <Alert variant="danger">{MODEL_NAMES_FAILED_TEXT}</Alert>}
    {isErrorAssignees && (
      <Alert variant="danger">{ASSIGNEE_NAMES_FAILED_TEXT}</Alert>
    )}
  </>
);

// The table's own assignee filter mounts a second observer of the CRM users
// read, which refetches a failed read and sets it pending again. Without this
// latch the page would fall back to <Loading /> (unmounting the filter) and
// loop. Loading shows until the three reads first settle, and not again.
const useInitialLoading = (isPending: boolean) => {
  const [settled, setSettled] = useState(false);
  if (!isPending && !settled) {
    setSettled(true);
  }
  return isPending && !settled;
};

// The same observer refetch clears the error of a failed read while it runs.
// The failure stays reported until the read returns data, so the alert does not
// flicker.
const useReadFailed = (isError: boolean, hasData: boolean) => {
  const [sawError, setSawError] = useState(false);
  if (isError && !sawError) {
    setSawError(true);
  }
  return isError || (sawError && !hasData);
};

export const HouseholdsTable = () => {
  const { filteredIds, isResolving } = useHouseholdInsightFilter();
  const slantConnected = useSlantConnected();

  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: dataHouseholds,
  } = useQueryHouseholds({
    includeBalances: true,
    includeCRM: true,
    includeInactive: true,
  });
  const households = dataHouseholds?.data;

  const {
    isPending: isPendingModels,
    isError: isErrorModels,
    data: dataModels,
  } = useQueryModels();

  const {
    isPending: isPendingAssignees,
    isError: isErrorAssignees,
    data: dataAssignees,
  } = useQueryCrmUsers();

  const householdsWithModel = useMemo(
    () =>
      (households ?? []).map((household) => ({
        ...household,
        model: (dataModels ?? []).find(
          (model) => model.id === household.assignedModelId,
        ),
        assignedUser: (dataAssignees?.data ?? []).find(
          (user) => user.id === household.assignedUserId,
        ),
      })),
    [households, dataModels, dataAssignees?.data],
  );

  // Include the Service Tier column only when the user is Slant-connected;
  // otherwise omit it entirely.
  const tableColumns = useMemo(
    () => (slantConnected ? [...columns, serviceTierColumn] : columns),
    [slantConnected],
  );

  const [tableSettings, setTableSettings] = useTableSettings("households", {
    sorting: [{ id: "name", desc: false }],
    filters: [
      {
        id: "isActive",
        value: ["Active"],
      },
    ],
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableSettings.filters,
  );
  const [sorting, setSorting] = useState<SortingState>(tableSettings.sorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: tableSettings.pageSize,
    pageIndex: 0,
  });

  const insightRows = useMemo(
    () => filterByInsightIds(householdsWithModel, filteredIds),
    [householdsWithModel, filteredIds],
  );
  const { filterBarItems, rows } = useHouseholdsCustomFieldsFilter({
    rows: insightRows,
    tableSettings,
    setTableSettings,
    setPagination,
  });

  const { table } = useTable({
    columns: tableColumns,
    data: rows,
    getRowId: householdRowId,
    state: { columnFilters, sorting, pagination },
    onColumnFiltersChange: onColumnFiltersChange(
      columnFilters,
      setColumnFilters,
      tableSettings,
      setTableSettings,
    ),
    onSortingChange: onSortingChange(
      sorting,
      setSorting,
      tableSettings,
      setTableSettings,
    ),
    onPaginationChange: onPaginationChange(
      pagination,
      setPagination,
      tableSettings,
      setTableSettings,
    ),
    autoResetAll: false,
  });

  const modelsFailed = useReadFailed(isErrorModels, dataModels !== undefined);
  const assigneesFailed = useReadFailed(
    isErrorAssignees,
    dataAssignees !== undefined,
  );

  const isLoading = useInitialLoading(
    isPendingHouseholds || isPendingModels || isPendingAssignees,
  );

  return isResolving || isLoading ? (
    <Loading />
  ) : isErrorHouseholds ? (
    <Alert variant="danger">An error occurred</Alert>
  ) : !households || households.length <= 0 ? (
    <Alert>No households found</Alert>
  ) : (
    <>
      <SupportingReadAlerts
        isErrorModels={modelsFailed}
        isErrorAssignees={assigneesFailed}
      />
      <Table table={table} filterBarItems={filterBarItems} />
    </>
  );
};

const EditableHouseholdsTable = ({
  rowSelection,
  setRowSelection,
  isLoading,
  isErrorHouseholds,
  isErrorModels,
  isErrorAssignees,
  householdsWithModel,
}: {
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  isLoading: boolean;
  isErrorHouseholds: boolean;
  isErrorModels: boolean;
  isErrorAssignees: boolean;
  householdsWithModel: HouseholdWithMetrics[];
}) => {
  const { filteredIds, isResolving } = useHouseholdInsightFilter();
  const slantConnected = useSlantConnected();

  const editableColumns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      }),
      ...columns,
      // Slant-connected users get the Service Tier column; others omit it.
      ...(slantConnected ? [serviceTierColumn] : []),
    ],
    [slantConnected],
  );

  const [tableSettings, setTableSettings] = useTableSettings("households", {
    sorting: [{ id: "name", desc: false }],
    filters: [
      {
        id: "isActive",
        value: ["Active"],
      },
    ],
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableSettings.filters,
  );
  const [sorting, setSorting] = useState<SortingState>(tableSettings.sorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: tableSettings.pageSize,
    pageIndex: 0,
  });

  const insightRows = useMemo(
    () => filterByInsightIds(householdsWithModel, filteredIds),
    [householdsWithModel, filteredIds],
  );
  const { filterBarItems, rows } = useHouseholdsCustomFieldsFilter({
    rows: insightRows,
    tableSettings,
    setTableSettings,
    setPagination,
    setRowSelection,
  });

  const { table } = useTable({
    columns: editableColumns,
    data: rows,
    getRowId: householdRowId,
    state: { rowSelection, columnFilters, sorting, pagination },
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: onColumnFiltersChange(
      columnFilters,
      setColumnFilters,
      tableSettings,
      setTableSettings,
    ),
    onSortingChange: onSortingChange(
      sorting,
      setSorting,
      tableSettings,
      setTableSettings,
    ),
    onPaginationChange: onPaginationChange(
      pagination,
      setPagination,
      tableSettings,
      setTableSettings,
    ),
    autoResetAll: false,
  });

  return isLoading || isResolving ? (
    <Loading />
  ) : isErrorHouseholds ? (
    <Alert variant="danger">An error occurred</Alert>
  ) : !householdsWithModel || householdsWithModel.length <= 0 ? (
    <Alert>No households found</Alert>
  ) : (
    <>
      <SupportingReadAlerts
        isErrorModels={isErrorModels}
        isErrorAssignees={isErrorAssignees}
      />
      <Table table={table} filterBarItems={filterBarItems} />
    </>
  );
};

const Households = () => {
  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: dataHouseholds,
  } = useQueryHouseholds({
    includeBalances: true,
    includeCRM: true,
    includeInactive: true,
  });

  const {
    isPending: isPendingModels,
    isError: isErrorModels,
    data: dataModels,
  } = useQueryModels();

  const {
    isPending: isPendingAssignees,
    isError: isErrorAssignees,
    data: dataAssignees,
  } = useQueryCrmUsers();

  const households = useMemo(
    () =>
      typeof dataHouseholds === "undefined"
        ? undefined
        : dataHouseholds.data.map((household) => ({
            ...household,
            model: (dataModels ?? []).find(
              (model) => model.id === household.assignedModelId,
            ),
            modelAssignmentStrategy: household.modelAssignmentStrategy,
            assignedUser: (dataAssignees?.data ?? []).find(
              (user) => user.id === household.assignedUserId,
            ),
          })),
    [dataAssignees?.data, dataHouseholds, dataModels],
  );

  const modelsFailed = useReadFailed(isErrorModels, dataModels !== undefined);
  const assigneesFailed = useReadFailed(
    isErrorAssignees,
    dataAssignees !== undefined,
  );

  const isLoading = useInitialLoading(
    isPendingHouseholds || isPendingModels || isPendingAssignees,
  );

  const modelOptions = isPendingModels
    ? [
        <option key={null} value="">
          Loading...
        </option>,
      ]
    : isErrorModels
      ? [
          <option key={null} value="">
            There was an error loading models
          </option>,
        ]
      : [
          <option key={null} value="">
            Select a model
          </option>,
          ...(dataModels ?? [])
            .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
            .map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            )),
        ];

  const updateHouseholdsModel = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<HouseholdsController["bulkAssignModelToHouseholds"]>
    >
  >(
    `/v1/households/model`,
    async (data: {
      modelId: number;
      households: { [key: number]: boolean };
    }) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const { setError, resetError } = useErrorMessage();
  const [rowSelection, setRowSelection] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedModelId, setModelId] = useState(0);

  const handleAssignModelClick = useCallback(() => {
    setModelId(0);
    setShowModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExportHouseholds();

  const onExport = useCallback(async () => {
    await refetchExport();
  }, [refetchExport]);

  const { mutateAsync: onSubmit, isPending: isPendingOnSubmit } = useMutation({
    // A household write, so every mounted `useQueryHouseholds` walks the list
    // again when it succeeds and shows the assigned model (APP-1653).
    mutationKey: ["PUT", "households", "model"],
    mutationFn: async (data: {
      modelId: number;
      households: { [key: number]: boolean };
    }) => {
      resetError();

      try {
        await updateHouseholdsModel(data);
        setRowSelection({});
      } catch (err) {
        const message = "Failed to assign model to households";
        setError(message);
        console.error(message, err);
      }
    },
  });

  const handleSaveChanges = useCallback(async () => {
    await onSubmit({
      modelId: selectedModelId,
      households: rowSelection,
    });
    setShowModal(false);
  }, [selectedModelId, rowSelection, onSubmit]);

  return (
    <TabContainerWithTabs tabs={ManageNav}>
      <Row className="mb-3">
        <Col>
          <SettlementWarning />
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                as={Link}
                to="/clients/households/new"
                label="New"
                icon="/icons/new.svg"
              />
            </ButtonGroup>
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Connect CRM"
                as={Link}
                icon="/icons/compare.svg"
                to="/clients/households/mapping"
              />
            </ButtonGroup>
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Export"
                icon="/icons/folded-list.svg"
                onClick={onExport}
                disabled={isLoadingExport}
              />
            </ButtonGroup>
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Assign Model"
                icon="/icons/assign.svg"
                onClick={handleAssignModelClick}
                disabled={Object.keys(rowSelection).length === 0}
              />
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                label="Create Report"
                as={Link}
                icon="/icons/bar-chart.svg"
                to={`/reports/batches/new?type=custom&${Object.keys(
                  rowSelection,
                )
                  .map((householdId) => `household_id=${householdId}`)
                  .join("&")}`}
                disabled={Object.keys(rowSelection).length <= 0}
                className={
                  Object.keys(rowSelection).length <= 0 ? "disabled" : ""
                }
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          <EditableHouseholdsTable
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            isErrorHouseholds={isErrorHouseholds}
            isErrorModels={modelsFailed}
            isErrorAssignees={assigneesFailed}
            isLoading={isLoading}
            householdsWithModel={households ?? []}
          />
        </Col>
        <ModelAssignDialog
          showModal={showModal}
          handleModalClose={handleModalClose}
          handleSaveChanges={handleSaveChanges}
          rowSelection={rowSelection}
          selectedModelId={selectedModelId}
          setModelId={setModelId}
          modelOptions={modelOptions}
          isSaving={isPendingOnSubmit}
          title="households"
        />
      </Row>
    </TabContainerWithTabs>
  );
};

export default Households;
